// Stránky investičních bytů – výpočty obou kalkulaček. Čisté funkce bez DOM,
// testy tests/redesign/reality-calc.test.mjs.
// Sazby a výchozí hodnoty jsou v datech projektu (includes/redesign/reality-<slug>.php),
// tady se nic nedoplňuje – funkce dostane, co má spočítat.

/**
 * Měsíční nájem ze sazby z kupní ceny. Výchozí zaokrouhlení na desetikoruny (Holzova, rentM() v návrhu),
 * `round === 'ceil'` = roční nájem na celé Kč a měsíční nahoru na celé Kč (Horní Lipová, xlsx V6) –
 * stejně jako efi_rd_reality_monthly_rent() v includes/redesign/reality.php.
 */
export function monthlyRent(price, rate, round = '') {
  if (!price || !rate) {
    return 0;
  }

  if (round === 'ceil') {
    return Math.ceil(Math.round(price * rate) / 12 - 1e-9);
  }

  return Math.round((price * rate) / 12 / 10) * 10;
}

/** Výnos z nájmu – dlouhodobý (nájem od e-Finance nebo vlastní) i krátkodobý provoz. */
export function computeRent({ price, mode, rent, adr, occ, efiYield, shortCostShare, rentRound = '' }) {
  const efiRent = monthlyRent(price, efiYield, rentRound);
  const short = mode === 'short';
  let grossYear;
  let netYear;

  if (short) {
    grossYear = 365 * ((Number(occ) || 0) / 100) * (Number(adr) || 0);
    netYear = grossYear * (1 - shortCostShare);
  } else {
    const monthly = rent === null || rent === '' ? efiRent : Number(rent) || 0;
    grossYear = 12 * monthly;
    netYear = grossYear;
  }

  const yieldOf = (value) => (price ? (value / price) * 100 : null);

  return {
    mode: short ? 'short' : 'long',
    price,
    efiRent,
    grossYear,
    netYear,
    grossYield: yieldOf(grossYear),
    netYield: yieldOf(netYear),
  };
}

/** Růst hodnoty: cena se úročí složeně, nájem se sčítá lineárně. */
export function computeGrowth({ price, years, growth, rate }) {
  const month = monthlyRent(price, rate);
  const year = month * 12;
  const priceAfter = (n) => price * Math.pow(1 + growth / 100, n);

  const rows = [];
  for (let n = 1; n <= years; n += 1) {
    const value = priceAfter(n);
    const gain = value - price;
    const rentSum = year * n;
    rows.push({ year: n, price: value, gain, rent: rentSum, total: gain + rentSum });
  }

  const endPrice = priceAfter(years);
  const endGain = endPrice - price;
  const rentSum = year * years;

  return { price, month, year, rows, endPrice, endGain, rentSum, total: endGain + rentSum };
}

/* ── formátování ─────────────────────────────────────────────────────── */
// toLocaleString(LOCALE) vrací úzkou pevnou mezeru; reference ji nahrazuje běžnou pevnou,
// aby se čísla lámala stejně jako zbytek webu.
function plainSpaces(value) {
  return value.replace(/ /g, ' ');
}

// Jazyková verze stránky (<html lang="en-GB">, includes/redesign/i18n.php) – čísla a měna podle ní,
// jinak česky. Stejné tvary jako efi_i18n_czk() v PHP: „4 860 000 Kč“, „4,860,000 CZK“, „4.860.000 CZK“.
const LOCALE = (typeof document !== 'undefined' && /^(en|de|pl)/.test(document.documentElement.lang || ''))
  ? document.documentElement.lang
  : 'cs-CZ';
const LANG = LOCALE.slice(0, 2);

export function num(value, digits = 0) {
  return plainSpaces(Number(value).toLocaleString(LOCALE, { maximumFractionDigits: digits }));
}

/** Mezera před „%“: česky, německy a polsky pevná, anglicky žádná („4%“). */
export function pctSpace() {
  return LANG === 'en' ? '' : '\u00a0';
}

export function czk(value) {
  if (value == null) {
    return '—';
  }

  return `${num(value)} ${LANG === 'cs' ? 'Kč' : 'CZK'}`;
}

export function area(value) {
  const formatted = Number(value).toLocaleString(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return `${plainSpaces(formatted)} m²`;
}

export function pct(value) {
  if (value == null) {
    return '—';
  }

  const formatted = Number(value).toLocaleString(LOCALE, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  return `${plainSpaces(formatted)}${LANG === 'en' ? '' : ' '}%`;
}
